/**
 * TemplateTopBar Component
 *
 * Top action bar for the template editor showing:
 * - Mobile menu toggle
 * - Preview, duplicate, share actions when template is selected
 */

import React from 'react';
import {
  ContentCopy as DuplicateIcon,
  Menu as MenuIcon,
  Preview as PreviewIcon,
  Send as ShareIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip } from '@mui/material';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

interface TemplateTopBarProps {
  /** Whether a template is selected */
  hasSelectedTemplate: boolean;
  /** Whether sidebar is visible (mobile) */
  isSidebarVisible: boolean;
  /** Callback to toggle sidebar */
  onToggleSidebar: () => void;
  /** Callback to open preview */
  onPreview: () => void;
  /** Callback to duplicate template */
  onDuplicate: () => void;
  /** Callback to open share modal */
  onShare: () => void;
}

export const TemplateTopBar: React.FC<TemplateTopBarProps> = ({
  hasSelectedTemplate,
  isSidebarVisible,
  onToggleSidebar,
  onPreview,
  onDuplicate,
  onShare,
}) => {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left Section - Mobile Menu Toggle */}
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <IconButton
          onClick={onToggleSidebar}
          aria-label={isSidebarVisible ? 'Hide menu' : 'Show menu'}
          sx={{ color: 'primary.main' }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Spacer for desktop */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Right Section - Template Actions */}
      {hasSelectedTemplate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <Tooltip title="Preview template">
            <IconButton
              onClick={onPreview}
              sx={{ color: 'primary.main' }}
              aria-label="Preview template"
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Duplicate template">
            <IconButton
              onClick={onDuplicate}
              sx={{ color: 'primary.main' }}
              aria-label="Duplicate template"
            >
              <DuplicateIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={onShare}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            Share
          </Button>

          {/* Mobile share icon button */}
          <Tooltip title="Share template">
            <IconButton
              onClick={onShare}
              sx={{
                color: 'primary.main',
                display: { xs: 'flex', sm: 'none' },
              }}
              aria-label="Share template"
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
